import type { CSSProperties } from "react";

/**
 * A block's inline styles, stored exactly as the markup wrote them.
 *
 * Keys are CSS property names (lower-cased, hyphenated — custom properties keep
 * their case); values are the declaration text verbatim. Storing the source
 * spelling rather than the browser's parsed CSSOM view is what lets a designed
 * template survive the trip through the document: `#1A1A1A` stays `#1A1A1A`
 * instead of becoming `rgb(26, 26, 26)`, and a property we have no name for —
 * every `mso-*` declaration, say — is kept rather than silently dropped.
 */
export type StyleMap = Record<string, string>;

/**
 * The default for every block's `style` attr.
 *
 * One shared frozen object, so a block that never carried inline styles is
 * identity-equal to the default and `renderHTML` can leave it out of
 * `data-attrs`.
 */
export const EMPTY_STYLE_MAP: StyleMap = Object.freeze({}) as StyleMap;

/**
 * The named style attrs blocks carried before the map existed.
 *
 * Documents saved then still spell their styles this way, so they stay in the
 * schema and are still applied — the map simply wins where both are present.
 */
export interface BlockStyleAttrs {
  backgroundColor: string;
  /** Per-block typeface, e.g. a designed template's own font stack. */
  fontFamily: string;
  fontSize: string;
  color: string;
  fontWeight: string;
  lineHeight: string;
  textDecoration: string;
  borderRadius: string;
  borderWidth: string;
  borderStyle: string;
  borderColor: string;
  paddingTop: string;
  paddingBottom: string;
  paddingLeft: string;
  paddingRight: string;
  marginTop: string;
  marginBottom: string;
}

const LEGACY_STYLE_KEYS: (keyof BlockStyleAttrs)[] = [
  "backgroundColor",
  "fontFamily",
  "fontSize",
  "color",
  "fontWeight",
  "lineHeight",
  "textDecoration",
  "borderRadius",
  "borderWidth",
  "borderStyle",
  "borderColor",
  "paddingTop",
  "paddingBottom",
  "paddingLeft",
  "paddingRight",
  "marginTop",
  "marginBottom",
];

/* ── Property-name conversions ────────────────────────────────────────── */

/**
 * The CSSOM spelling of a CSS property name, which is also the spelling React
 * hyphenates back to the original: `mso-line-height-rule` →
 * `msoLineHeightRule` → `mso-line-height-rule`. Going through camel case rather
 * than handing React the hyphenated name avoids its "Unsupported style
 * property" warning while producing identical output.
 */
export function toStylePropertyName(property: string): string {
  if (property.startsWith("--") || !property.includes("-")) return property;
  const vendor = property.startsWith("-");
  const [head, ...tail] = (vendor ? property.slice(1) : property).split("-");
  const rest = tail
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");
  if (!vendor) return head + rest;
  // React re-adds the leading dash for `ms`; every other vendor prefix needs it
  // spelled with a capital (`WebkitTransform` → `-webkit-transform`).
  if (head === "ms") return head + rest;
  return head.charAt(0).toUpperCase() + head.slice(1) + rest;
}

/** The CSS spelling of a JS style property name — the inverse of the above. */
export function toCssPropertyName(property: string): string {
  if (property.startsWith("--")) return property;
  return property
    .replace(/([A-Z])/g, "-$1")
    .toLowerCase()
    .replace(/^ms-/, "-ms-");
}

/**
 * Every shorthand a property is part of, longest first: `border-top-width`
 * belongs to `border-top` and to `border`.
 */
function shorthandsOf(segments: string[], separator: string): string[] {
  const out: string[] = [];
  for (let i = segments.length - 1; i > 0; i--) {
    out.push(segments.slice(0, i).join(separator));
  }
  return out;
}

/** The shorthands of a CSS-spelled property: `padding-left` → `padding`. */
function cssShorthandsOf(property: string): string[] {
  return shorthandsOf(property.split("-"), "-");
}

/** The shorthands of a JS-spelled property: `paddingLeft` → `padding`. */
function styleShorthandsOf(property: string): string[] {
  return shorthandsOf(property.split(/(?=[A-Z])/), "");
}

/**
 * Set a longhand, dropping any shorthand already on the object that it would
 * otherwise fight with — React warns when `margin` and `marginTop` are both
 * present, and the resolved value depends on key order rather than on CSS
 * cascade rules.
 *
 * `written` is what this same pass already set, and is exempt: a source that
 * says `border: none; border-top: 2px solid #ccc` means both, and that is how
 * email draws a rule.
 */
function setStyle(
  target: Record<string, unknown>,
  property: string,
  value: string,
  written: Set<string>,
): void {
  for (const shorthand of styleShorthandsOf(property)) {
    if (shorthand in target && !written.has(shorthand))
      delete target[shorthand];
  }
  target[property] = value;
  written.add(property);
}

/* ── Reading and writing a block's styles ─────────────────────────────── */

/** A block's as-written style map, or an empty one. */
export function getStyleMap(attrs: Record<string, unknown>): StyleMap {
  const stored = attrs.style;
  if (!stored || typeof stored !== "object") return EMPTY_STYLE_MAP;
  return stored as StyleMap;
}

/**
 * Apply a block's as-written styles onto a CSSProperties object.
 *
 * For blocks whose named attrs are their own vocabulary rather than CSS — a
 * button's `borderRadius` can be the preset `"lg"`, a blockquote's
 * `borderColor` paints one edge — this is the whole of it: the block resolves
 * its own attrs into base styles, and the map goes on top.
 */
export function applyStyleMap(
  styles: CSSProperties,
  attrs: Record<string, unknown>,
  written: Set<string> = new Set(),
): void {
  const target = styles as Record<string, unknown>;
  for (const [property, value] of Object.entries(getStyleMap(attrs))) {
    if (typeof value === "string" && value) {
      setStyle(target, toStylePropertyName(property), value, written);
    }
  }
}

/**
 * Paint styles onto a NodeView's element, clearing the ones the last paint set
 * that are gone now.
 *
 * `Object.assign` alone only ever adds: remove a section's background image or
 * padding and the canvas kept showing it until reload, while the email sent
 * without it. Returns the keys painted, to pass back in on the next update.
 */
export function paintStyles(
  el: HTMLElement,
  styles: CSSProperties,
  previous: ReadonlySet<string> = new Set(),
): Set<string> {
  const target = el.style as unknown as Record<string, string>;
  for (const key of previous) {
    if (!(key in styles)) target[key] = "";
  }
  Object.assign(el.style, styles);
  return new Set(Object.keys(styles));
}

/**
 * Apply a text block's styles: the legacy named attrs first, then the
 * as-written map on top, so a document that carries both resolves to whichever
 * was stored most recently.
 *
 * Only `emailParagraph` and `emailHeading` ever wrote the named attrs, and they
 * spell them exactly as CSS does — every other block uses `applyStyleMap`.
 */
export function applyBlockStyles(
  styles: CSSProperties,
  attrs: Record<string, unknown>,
): void {
  const target = styles as Record<string, unknown>;
  const written = new Set<string>();
  for (const key of LEGACY_STYLE_KEYS) {
    const value = attrs[key];
    if (typeof value === "string" && value) {
      setStyle(target, key, value, written);
    }
  }
  applyStyleMap(styles, attrs, written);
}

/**
 * A block's value for one style property: the as-written map first, the legacy
 * named attr second.
 *
 * `cssProperty` is for the blocks whose attr name is their own vocabulary
 * rather than the CSS one — a button's `paddingHorizontal` is `padding-left`,
 * a divider's `thickness` is `border-top-width`.
 */
export function readStyle(
  attrs: Record<string, unknown>,
  property: string,
  cssProperty: string = toCssPropertyName(property),
): string {
  const stored = getStyleMap(attrs)[cssProperty];
  if (typeof stored === "string" && stored) return stored;
  const named = attrs[property];
  return typeof named === "string" ? named : "";
}

/**
 * Drop a property from a style map along with every shorthand and longhand that
 * would keep overriding it — clearing `padding-left` has to take `padding` with
 * it, or the shorthand simply paints the old value back.
 */
function dropStyle(map: StyleMap, cssProperty: string): StyleMap {
  const doomed = new Set([cssProperty, ...cssShorthandsOf(cssProperty)]);
  const prefix = `${cssProperty}-`;
  const next: StyleMap = {};
  for (const [key, value] of Object.entries(map)) {
    if (doomed.has(key) || key.startsWith(prefix)) continue;
    next[key] = value;
  }
  return next;
}

/**
 * The attr patch for editing one style property.
 *
 * `target: "named"` writes the block's own attr (a button's `paddingHorizontal`
 * stays the button's vocabulary); `target: "map"` writes the style map, which
 * is what a control over a plain CSS property does. Either way the other tier's
 * copy is removed, so the edit is what renders.
 */
export function writeStyle(
  attrs: Record<string, unknown>,
  property: string,
  value: string,
  options: { target?: "named" | "map"; cssProperty?: string } = {},
): Record<string, unknown> {
  const target = options.target ?? "map";
  const cssProperty = options.cssProperty ?? toCssPropertyName(property);
  const map = dropStyle(getStyleMap(attrs), cssProperty);

  if (target === "named") return { [property]: value, style: map };
  return {
    [property]: "",
    style: value ? { ...map, [cssProperty]: value } : map,
  };
}
